/**
 * @file inward-procurement-prices.tsx
 * @module app/modules/inward/components/inward-procurement-prices
 * @description Procurement section of an Inward GRN (permission "Acknowledge Cost Price"):
 *   - cost price changes made by Finance (Tier 1 alert) to acknowledge;
 *   - products whose cost is above the selling price (negative GP): they stay out of stock
 *     until procurement sets a new selling price, the same as or above the cost, with a note.
 *     When a cost change is pending for the product, both are done in one step.
 *   Saving the selling price updates the Price Catalog (Tier 3 alert) and the product goes
 *   live once Warehouse has approved. Others see what is waiting, without inputs.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useState } from "react";
import { Button, Card, Form, Table } from "react-bootstrap";
import Method from "../../../../utils/methods";
import {
  checkSellingProposal,
  costNeedsSelling,
  gpPercent,
  ReceivedLine,
} from "../utils/inward-approval";

interface InwardProcurementPricesProps {
  /** pending cost changes (Tier 1) */
  costChanges: ReceivedLine[];
  /** held back, no cost change pending: only the selling price is needed */
  sellingUpdates: ReceivedLine[];
  canAct: boolean;
  busyKey: string;
  /** acknowledge a cost change; selling price + note when the cost is above it */
  onAcknowledge: (
    line: ReceivedLine,
    selling?: { sellingPrice: number; note: string }
  ) => void;
  onUpdateSelling: (
    line: ReceivedLine,
    selling: { sellingPrice: number; note: string }
  ) => void;
  onError: (message: string) => void;
}

const money = (n: number | null | undefined) =>
  n === null || n === undefined ? "-" : `TSh ${Method.getGeneralizedAmount(n)}`;

const InwardProcurementPrices = ({
  costChanges,
  sellingUpdates,
  canAct,
  busyKey,
  onAcknowledge,
  onUpdateSelling,
  onError,
}: InwardProcurementPricesProps) => {
  const [inputs, setInputs] = useState<
    Record<string, { price: string; note: string }>
  >({});
  const rows = [...costChanges, ...sellingUpdates];
  if (rows.length === 0) return null;

  const valueOf = (key: string) => inputs[key] ?? { price: "", note: "" };
  const setValue = (key: string, field: "price" | "note", value: string) =>
    setInputs((prev) => ({
      ...prev,
      [key]: { ...valueOf(key), [field]: value },
    }));

  const submit = (line: ReceivedLine, isCostChange: boolean) => {
    const cost = isCostChange
      ? (line.costChange?.invoiceCost as number)
      : line.costPrice;
    const needsPrice = !isCostChange || costNeedsSelling(line);
    if (!needsPrice) {
      onAcknowledge(line);
      return;
    }
    const v = valueOf(line.key);
    const problem = checkSellingProposal(v.price, v.note, cost);
    if (problem) {
      onError(problem);
      return;
    }
    const selling = { sellingPrice: Number(v.price), note: v.note.trim() };
    if (isCostChange) onAcknowledge(line, selling);
    else onUpdateSelling(line, selling);
  };

  return (
    <Card
      className="inward-card inward-card--alert mb-5"
      data-testid="grn-procurement"
    >
      <Card.Body>
        <h2 className="fs-18 fw-bold mb-1">
          Procurement · cost and selling price
        </h2>
        <p className="fs-14 text-muted mb-3">
          A product whose cost is above its selling price stays out of stock
          until a new selling price (same as or above the cost) is saved with a
          note. Saving updates the Price Catalog and adds a Tier 3 alert there;
          acknowledging a cost change also updates the PO and clears its Tier 1
          alert.
        </p>
        <div className="table-responsive">
          <Table className="align-middle fs-14 mb-0">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-end">PO cost</th>
                <th className="text-end">Cost price</th>
                <th className="text-end">Selling price now</th>
                <th>New selling price · note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const isCostChange = costChanges.includes(l);
                const cost = isCostChange
                  ? (l.costChange?.invoiceCost as number)
                  : l.costPrice;
                const needsPrice = !isCostChange || costNeedsSelling(l);
                const v = valueOf(l.key);
                const gp = gpPercent(
                  v.price === "" ? null : Number(v.price),
                  cost
                );
                return (
                  <tr key={l.key} data-testid={`procurement-${l.key}`}>
                    <td>
                      <div className="fw-semibold">{l.title}</div>
                      <div className="fs-12 text-muted">{l.unit}</div>
                      <div
                        className={`fs-12 fw-semibold ${
                          needsPrice ? "text-danger" : "text-muted"
                        }`}
                        data-testid="procurement-reason"
                      >
                        {isCostChange
                          ? needsPrice
                            ? "Cost change (Tier 1) · cost above selling price"
                            : "Cost change (Tier 1)"
                          : "Cost above selling price"}
                      </div>
                    </td>
                    <td className="text-end">
                      {money(l.costChange?.poCost ?? l.poCostPrice)}
                    </td>
                    <td className="text-end fw-bold">{money(cost)}</td>
                    <td
                      className={`text-end ${
                        needsPrice ? "text-danger fw-bold" : ""
                      }`}
                    >
                      {money(l.newSellingPrice)}
                    </td>
                    <td style={{ minWidth: 260 }}>
                      {needsPrice && canAct ? (
                        <div className="d-flex flex-column gap-2">
                          <Form.Control
                            size="sm"
                            type="number"
                            inputMode="decimal"
                            min={cost}
                            placeholder={`At least ${cost}`}
                            value={v.price}
                            onChange={(e) =>
                              setValue(l.key, "price", e.target.value)
                            }
                            aria-label={`New selling price for ${l.title}`}
                            data-testid="procurement-price"
                          />
                          <Form.Control
                            size="sm"
                            as="textarea"
                            rows={2}
                            placeholder="Note (required): why the selling price changes"
                            value={v.note}
                            onChange={(e) =>
                              setValue(l.key, "note", e.target.value)
                            }
                            aria-label={`Note for ${l.title}`}
                            data-testid="procurement-note"
                          />
                          {gp !== null && (
                            <span
                              className={`fs-12 fw-semibold ${
                                gp < 0 ? "text-danger" : "text-success"
                              }`}
                              data-testid="procurement-gp"
                            >
                              GP {gp}%
                            </span>
                          )}
                        </div>
                      ) : needsPrice ? (
                        <span className="text-muted">
                          Waiting for procurement
                        </span>
                      ) : (
                        <span className="text-muted">
                          No selling price change needed
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      {canAct ? (
                        <Button
                          variant="danger"
                          className="inward-touch-btn"
                          disabled={busyKey === l.key}
                          onClick={() => submit(l, isCostChange)}
                          data-testid="procurement-submit"
                        >
                          {busyKey === l.key
                            ? "Saving…"
                            : isCostChange
                            ? needsPrice
                              ? "Acknowledge & update price"
                              : "Acknowledge"
                            : "Update selling price"}
                        </Button>
                      ) : (
                        <span className="text-muted">
                          Waiting for procurement
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default InwardProcurementPrices;
